// src/views/SettingsPage.js
import React, { useEffect } from 'react'; 
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    User, Users, UserX, Building, Briefcase, CreditCard, 
    ClipboardList, CalendarClock, Zap, Link as LinkIcon, 
    Tag, Wrench, Code, Archive, UploadCloud, ChevronRight, Bell, RotateCcw, Map, FileText, Wallet, ShieldCheck, Hash, ListOrdered, ClipboardCheck, Percent,
    BookOpen, // catscreen-7a2c4e91
    ScrollText, // doced-8f3a61c2
} from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

import CurrentUserProfile from '../components/crm/CurrentUserProfile';
import CompanySettings from '../components/crm/CompanySettings';
import ServicesItems from '../components/crm/ServicesItems';
import OrphanedCustomers from '../components/crm/OrphanedCustomers';
import PaymentSettings from '../components/crm/PaymentSettings';
import CustomFieldsSettings from '../components/crm/CustomFieldsSettings';
import PayrollSettings from '../components/crm/PayrollSettings';
import IntegrationsPage from '../components/settings/IntegrationsPage';
import TagManagementSettings from '../components/settings/TagManagementSettings';
import ResourceManagementSettings from '../components/settings/ResourceManagementSettings';
import EmbeddableFormSettings from '../components/settings/EmbeddableFormSettings';
import ArchivedCustomers from '../components/settings/ArchivedCustomers';
import BillingSettings from '../components/settings/BillingSettings'; 
import ImportWizardV2 from '../components/settings/ImportWizardV2';
import CommunicationsSettings from '../components/settings/CommunicationsSettings';
import RouteGroupSettings from '../components/settings/RouteGroupSettings';
import JobTemplatesSettings from '../components/settings/JobTemplatesSettings';
import DepositSettings from '../components/settings/DepositSettings';
import PipelineSettings from '../components/settings/PipelineSettings'; // qpipe-4d1a7c35-settings-import
import FollowUpVocabularySettings, { CLOSE_GROUP } from '../components/settings/FollowUpVocabularySettings'; // qfup-7d3e9a41-settings-import, qclose-3e8a5c17
import NumberingSettings from '../components/settings/NumberingSettings'; // nb-3e7a1f92
import TechPermissionsSettings from '../components/settings/TechPermissionsSettings';
import ChecklistSettings from '../components/settings/ChecklistSettings'; // chklstbuild-6c1e9b42
import { isChecklistBuilderOn } from '../utils/checklistTemplateDraft';
import TaxSettings from '../components/settings/TaxSettings'; // taxcodes-8d4f1a6e
import { isTaxCodesOn, canEditTaxes } from '../utils/taxCodes';
import { useTaxCodesDoc } from '../utils/taxExemptionUi'; // taxpick4-54573069
import { HIGH_LEVEL_ROLES } from '../utils/permissions';
import CatalogSettings from '../components/settings/catalog/CatalogSettings'; // catscreen-7a2c4e91
import { isCatalogScreenOn, canUseCatalog } from '../utils/catalogUi';
import DocumentSettings from '../components/settings/DocumentSettings'; // doced-8f3a61c2
import { isDocumentPageOn } from '../utils/documentPages';

const SettingsPage = ({ 
    user, 
    currentUserProfile, 
    companySettings, 
    isGmailConnected, 
    isCalendarConnected, 
    isOutlookConnected, 
    isMsCalendarConnected,
    // mailtok-2c7f9e51 — onConnectMicrosoft removed: it forwarded CrmApp's dead Firebase-popup
    // connect, and IntegrationsPage never destructured it (it uses its own handleMicrosoftConnect).
    activeTab,
    onTabChange 
}) => {
    
    const role = currentUserProfile?.role || '';
    // --- PERMISSION GROUPS ---
    const canViewCustomerTools = ['Admin', 'Manager', 'Owner/System Admin', 'Owner', 'System Admin'].includes(role);
    const canViewAdminSettings = ['Admin', 'Owner/System Admin', 'Owner', 'System Admin'].includes(role);

    // chklstbuild-6c1e9b42 — Settings › Checklists: Owner / Admin / Manager in every spelling
    // (HIGH_LEVEL_ROLES = the server's BUILDER_ROLES), and only where the roll-out switch is on.
    // Deliberately NOT in the restrictedTabs lists below: those are Owner/Admin only.
    const canBuildChecklists = HIGH_LEVEL_ROLES.includes(role) && isChecklistBuilderOn(currentUserProfile?.organizationId);
    useEffect(() => {
        if (activeTab === 'checklists' && !canBuildChecklists) onTabChange('currentUser');
    }, [activeTab, canBuildChecklists, onTabChange]);

    // taxcodes-8d4f1a6e — Settings › Taxes: Owner / Admin / Manager / Office Staff (the server's
    // EDIT_ROLES), and only where the roll-out switch is on. Not in restrictedTabs (Owner/Admin only).
    // taxpick4-54573069 — the switch is read from settings/taxCodes (missing or still loading = the legacy list).
    const taxCodesDoc = useTaxCodesDoc(currentUserProfile?.organizationId);
    const canSeeTaxes = canEditTaxes(role) && isTaxCodesOn(currentUserProfile?.organizationId, taxCodesDoc === undefined ? null : taxCodesDoc);
    useEffect(() => {
        if (activeTab === 'taxes' && !canSeeTaxes) onTabChange('currentUser');
    }, [activeTab, canSeeTaxes, onTabChange]);

    // catscreen-7a2c4e91 — Settings › Catalog replaces Line Items where the company's switch is on
    // (organizations/{org}.catalogScreenEnabled, set only by functions/scripts/setCatalogScreen.js),
    // for Owner / Admin / Manager (catroll-5c8e2a71: Office Staff removed; on unless the company is
    // explicitly switched off). Switch off: Line Items exactly as before.
    // The bounce waits for the company doc (companySettings) so nobody is moved before it loads.
    const catalogOn = isCatalogScreenOn(companySettings);
    const canSeeCatalog = catalogOn && canUseCatalog(role);
    useEffect(() => {
        // CrmApp starts companySettings as {} until the company doc arrives.
        if (!companySettings || Object.keys(companySettings).length === 0) return;
        if (activeTab === 'catalog' && !canSeeCatalog) onTabChange('currentUser');
        if (activeTab === 'services' && catalogOn) onTabChange(canSeeCatalog ? 'catalog' : 'currentUser');
    }, [activeTab, catalogOn, canSeeCatalog, companySettings, onTabChange]);

    // doced-8f3a61c2 — Settings › Documents (default estimate Terms): Owner / Admin / Manager (HIGH_LEVEL_ROLES =
    // firestore.rules isHighLevel, who may write documentTerms), and only where the new estimate page is on —
    // the default is used only when that page creates an estimate, so elsewhere it would do nothing.
    // Not in restrictedTabs (Owner/Admin only); waits for the company doc like Catalog.
    const canSeeDocuments = HIGH_LEVEL_ROLES.includes(role) && isDocumentPageOn(companySettings, 'estimates');
    useEffect(() => {
        if (!companySettings || Object.keys(companySettings).length === 0) return;
        if (activeTab === 'documents' && !canSeeDocuments) onTabChange('currentUser');
    }, [activeTab, canSeeDocuments, companySettings, onTabChange]);

    const location = useLocation();

    // --- FIX 1: SAFETY BOUNCE ---
    // If a non-admin lands on a restricted tab, force them to 'currentUser'
    useEffect(() => {
        const restrictedTabs = ['billing', 'companyInfo', 'payments', 'deposits', 'pipeline', 'techPermissions', 'payroll', 'migration', 'communications', 'numbering']; // nb-8c4d2b06 qpipe-4d1a7c35
        if (!canViewAdminSettings && restrictedTabs.includes(activeTab)) {
            console.log(`Redirecting unauthorized access from ${activeTab} to currentUser`);
            onTabChange('currentUser');
        }
    }, [activeTab, canViewAdminSettings, onTabChange]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');
        const isSuccess = searchParams.get('success');
        const isReauth = searchParams.get('reauth');

        if (canViewAdminSettings) {
            if (tabParam === 'billing' && activeTab !== 'billing') {
                onTabChange('billing');
            }
            if (tabParam === 'companyInfo' && activeTab !== 'companyInfo') {
                onTabChange('companyInfo');
            }
            if ((tabParam === 'payments' || isSuccess || isReauth) && activeTab !== 'payments') {
                onTabChange('payments');
            }
        }
    }, [location, activeTab, onTabChange, canViewAdminSettings]);

    // Locked-out state — owner whose subscription is not active or trialing
    const isPayer = ['Owner', 'Owner/System Admin', 'System Admin', 'Admin'].includes(role);
    const isLockedOut = isPayer && !['active', 'trialing'].includes(currentUserProfile?.subscriptionStatus);
    // catscreen-7a2c4e91 — the menu sits next to the app's nav on every tab and the content takes the
    // rest. Other tabs keep a reading width; the Catalog (folders + a 9-column table) uses all of it.
    const fullWidthTab = activeTab === 'catalog' && canSeeCatalog && !isLockedOut;

    const renderTabContent = () => {
        // Double check permission at render time for safety
        if (!canViewAdminSettings && ['billing', 'companyInfo', 'payments', 'deposits', 'pipeline', 'techPermissions', 'communications', 'numbering'].includes(activeTab)) { // nb-5a9f7d31 qpipe-4d1a7c35
            return <CurrentUserProfile user={user} profile={currentUserProfile} />;
        }

        switch (activeTab) {
            case 'currentUser': return <CurrentUserProfile user={user} profile={currentUserProfile} />;
            case 'billing': return <BillingSettings currentUserProfile={currentUserProfile} />;
            case 'companyInfo': return <CompanySettings currentUserProfile={currentUserProfile} onOpenTaxes={canSeeTaxes ? () => onTabChange('taxes') : undefined} />;
            case 'taxes': return canSeeTaxes // taxcodes-8d4f1a6e
                ? <TaxSettings currentUserProfile={currentUserProfile} companySettings={companySettings} />
                : <CurrentUserProfile user={user} profile={currentUserProfile} />;
            case 'payments': return <PaymentSettings currentUserProfile={currentUserProfile} />;
            case 'deposits': return <DepositSettings currentUserProfile={currentUserProfile} />;
            case 'documents': return canSeeDocuments // doced-8f3a61c2
                ? <DocumentSettings currentUserProfile={currentUserProfile} />
                : <CurrentUserProfile user={user} profile={currentUserProfile} />;
            // qfup-7d3e9a41-settings-case — Seam 6 follow-up options sit on the
            // pipeline tab, under the stage list: both describe how an estimate
            // is worked, and both are Owner/Admin/Manager-gated the same way.
            case 'pipeline': return (
                <div className="space-y-6">
                    <PipelineSettings currentUserProfile={currentUserProfile} />
                    <FollowUpVocabularySettings currentUserProfile={currentUserProfile} />
                    {/* qclose-3e8a5c17 — Seam 9b: the close lists, same editor,
                        same document; this panel sends only its own two lists. */}
                    <FollowUpVocabularySettings currentUserProfile={currentUserProfile} group={CLOSE_GROUP} />
                </div>
            );
            case 'numbering': return <NumberingSettings currentUserProfile={currentUserProfile} />; // nb-1d6e4c83
            case 'techPermissions': return <TechPermissionsSettings currentUserProfile={currentUserProfile} />;
            case 'payroll': return <PayrollSettings currentUserProfile={currentUserProfile} />;
            case 'services': return catalogOn // catscreen-7a2c4e91 — never the old page once the switch is on
                ? <CurrentUserProfile user={user} profile={currentUserProfile} />
                : <ServicesItems currentUserProfile={currentUserProfile} />; // (the bounce above then moves to Catalog)
            case 'catalog': return canSeeCatalog // catscreen-7a2c4e91
                ? <CatalogSettings currentUserProfile={currentUserProfile} companySettings={companySettings} />
                : <CurrentUserProfile user={user} profile={currentUserProfile} />;
            case 'resources': return <ResourceManagementSettings currentUserProfile={currentUserProfile} />;
            case 'websiteForm': return <EmbeddableFormSettings currentUserProfile={currentUserProfile} />;
            case 'orphanedCustomers': return <OrphanedCustomers currentUserProfile={currentUserProfile} />;
            case 'archived': return <ArchivedCustomers currentUserProfile={currentUserProfile} />;
            case 'customFields': return <CustomFieldsSettings currentUserProfile={currentUserProfile} />;
            case 'tags': return <TagManagementSettings currentUserProfile={currentUserProfile} />;
            case 'communications': return <CommunicationsSettings currentUserProfile={currentUserProfile} />;
            case 'routeGroups': return <RouteGroupSettings currentUserProfile={currentUserProfile} />;
            case 'jobTemplates': return <JobTemplatesSettings currentUserProfile={currentUserProfile} />;
            case 'checklists': return canBuildChecklists // chklstbuild-6c1e9b42
                ? <ChecklistSettings currentUserProfile={currentUserProfile} />
                : <CurrentUserProfile user={user} profile={currentUserProfile} />;
            case 'integrations':
                return <IntegrationsPage 
                            user={user}
                            currentUserProfile={currentUserProfile} 
                            companySettings={companySettings}
                            isGmailConnected={isGmailConnected}
                            isCalendarConnected={isCalendarConnected}
                            isOutlookConnected={isOutlookConnected}
                            isMsCalendarConnected={isMsCalendarConnected}
                        />;
            // h1inv-6d3a9f27 — the "Migration (legacy)" tab is retired (founder decision 2026-10-07; it never
            // created an invoice). It wrote invoice numbers and import fields from the browser, which the
            // invoice rules now refuse. Data Import (ImportWizardV2) imports through the server.
            case 'dataImport': return <ImportWizardV2 currentUserProfile={currentUserProfile} />;    
            default: return <CurrentUserProfile user={user} profile={currentUserProfile} />;
        }
    };

    const NavLink = ({ tabName, icon, children, id }) => (
        <button
            id={id}
            data-settings-tab={tabName} // qpipe-4d1a7c35-leaveguard — lets a tab with unsaved edits (PipelineSettings) ask before switching
            onClick={() => onTabChange(tabName)}
            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg w-full text-left transition-all ${
                activeTab === tabName
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
        >
            <div className={`${activeTab === tabName ? 'text-white' : 'text-gray-400'}`}>
                {icon}
            </div>
            <span className="flex-grow">{children}</span>
            {activeTab === tabName && <ChevronRight size={16} className="text-white opacity-50" />}
        </button>
    );

    const documentsNavLink = ( // doced-8f3a61c2
        <NavLink tabName="documents" id="tab-documents" icon={<ScrollText size={18}/>}>
            Documents <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-[#5F4EC9]/10 text-[#5F4EC9]">New</span>
        </NavLink>
    );

    const taxesNavLink = ( // taxcodes-8d4f1a6e
        <NavLink tabName="taxes" id="tab-taxes" icon={<Percent size={18}/>}>
            Taxes <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-[#5F4EC9]/10 text-[#5F4EC9]">New</span>
        </NavLink>
    );

    // When locked out, force billing tab if not already there
    useEffect(() => {
        if (isLockedOut && activeTab !== 'billing') {
            onTabChange('billing');
        }
    }, [isLockedOut, activeTab, onTabChange]);

    return (
        <div className="p-4 sm:p-6 md:p-10">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Settings</h1>

            {/* Locked-out banner */}
            {isLockedOut && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                        <CreditCard size={16} className="text-red-600" />
                    </div>
                    <div>
                        <p className="font-semibold text-red-800">Your trial has ended</p>
                        <p className="text-sm text-red-600 mt-0.5">Add a payment method below to restore full access to Spatchy.</p>
                    </div>
                </div>
            )}
            
            <div className="flex flex-col lg:flex-row gap-8">
                {/* --- NAVIGATION MENU --- */}
                <div className="w-full lg:w-64 lg:flex-shrink-0">
                    <nav className="flex lg:flex-col space-x-2 lg:space-x-0 lg:space-y-1 overflow-x-auto pb-4 lg:pb-0 no-scrollbar">
                        {/* When locked out, only show Billing */}
                        {isLockedOut ? (
                            <NavLink tabName="billing" id="tab-billing" icon={<CreditCard size={18}/>}>Billing</NavLink>
                        ) : (
                        <>{/* --- FIX 2: Added ID to Profile --- */}
                        <NavLink tabName="currentUser" id="tab-profile" icon={<User size={18}/>}>Profile</NavLink>
                        
                        <NavLink tabName="integrations" id="tab-integrations" icon={<LinkIcon size={18}/>}>Integrations</NavLink>
                        
                        {canViewCustomerTools && (
                            <>
                                <NavLink tabName="orphanedCustomers" icon={<UserX size={18}/>}>Orphaned</NavLink>
                                <NavLink tabName="archived" icon={<Archive size={18}/>}>Archived</NavLink>
                            </>
                        )}

                        {canViewAdminSettings && (
                            <>
                                <NavLink tabName="billing" id="tab-billing" icon={<CreditCard size={18}/>}>Billing</NavLink>
                                <NavLink tabName="dataImport" icon={<UploadCloud size={18}/>}>Data Import</NavLink>
                                <NavLink tabName="companyInfo" id="tab-company" icon={<Building size={18}/>}>Company</NavLink>
                                {canSeeTaxes && taxesNavLink /* taxcodes-8d4f1a6e — right after Company */}
                                <NavLink tabName="payments" id="tab-payments" icon={<CreditCard size={18}/>}>Payments</NavLink>
                        <NavLink tabName="deposits" id="tab-deposits" icon={<Wallet size={18}/>}>Deposits</NavLink>
                                {canSeeDocuments && documentsNavLink /* doced-8f3a61c2 — right after Deposits */}
                                <NavLink tabName="pipeline" id="tab-pipeline" icon={<ListOrdered size={18}/>}>Pipeline</NavLink>{/* qpipe-4d1a7c35-settings-nav */}
                                <NavLink tabName="numbering" id="tab-numbering" icon={<Hash size={18}/>}>Numbering</NavLink>{/* nb-9b2f8a47 */}
                                <NavLink tabName="techPermissions" icon={<ShieldCheck size={18}/>}>Technician Permissions</NavLink>
                                <NavLink tabName="payroll" icon={<CalendarClock size={18}/>}>Payroll</NavLink>
                                {/* NEW: Job Reminder Settings */}
                                <NavLink tabName="communications" icon={<Bell size={18}/>}>Communications</NavLink>
                                <NavLink tabName="routeGroups" icon={<Map size={18}/>}>Route Groups</NavLink>
                                <NavLink tabName="jobTemplates" icon={<FileText size={18}/>}>Job Templates</NavLink>
                                <NavLink tabName="customFields" icon={<ClipboardList size={18}/>}>Fields</NavLink>
                                <NavLink tabName="tags" icon={<Tag size={18}/>}>Tags</NavLink>
                                {catalogOn /* catscreen-7a2c4e91 — Catalog takes Line Items' place */
                                    ? <NavLink tabName="catalog" id="tab-catalog" icon={<BookOpen size={18}/>}>Catalog <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-[#5F4EC9]/10 text-[#5F4EC9]">New</span></NavLink>
                                    : <NavLink tabName="services" id="tab-services" icon={<Briefcase size={18}/>}>Line Items</NavLink>}
                                <NavLink tabName="resources" icon={<Wrench size={18}/>}>Resources</NavLink>
                                <NavLink tabName="websiteForm" icon={<Code size={18}/>}>Web Form</NavLink>
                            </>
                        )}

                        {/* doced-8f3a61c2 — Managers don't see the admin tabs, so Documents stands on its own here */}
                        {canSeeDocuments && !canViewAdminSettings && documentsNavLink}

                        {/* taxcodes-8d4f1a6e — Managers and Office Staff don't see Company, so Taxes stands on its own here */}
                        {canSeeTaxes && !canViewAdminSettings && taxesNavLink}

                        {/* catscreen-7a2c4e91 — Managers and Office Staff get Catalog on its own (no admin tabs) */}
                        {canSeeCatalog && !canViewAdminSettings && (
                            <NavLink tabName="catalog" id="tab-catalog" icon={<BookOpen size={18}/>}>Catalog <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-[#5F4EC9]/10 text-[#5F4EC9]">New</span></NavLink>
                        )}

                        {/* chklstbuild-6c1e9b42 — own gate: Managers get this tab without the admin tabs */}
                        {canBuildChecklists && (
                            <NavLink tabName="checklists" id="tab-checklists" icon={<ClipboardCheck size={18}/>}>
                                Checklists <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-[#5F4EC9]/10 text-[#5F4EC9]">New</span>
                            </NavLink>
                        )}

                        {/* Restart setup guide — owner only */}
                        {canViewAdminSettings && (
                            <div className="pt-4 mt-4 border-t border-gray-100">
                                <button
                                    onClick={async () => {
                                        if (!currentUserProfile?.id) return;
                                        try {
                                            await updateDoc(doc(db, 'users', currentUserProfile.id), {
                                                onboardingCompleted: false
                                            });
                                            window.location.href = '/home';
                                        } catch (err) {
                                            console.error('Failed to restart onboarding:', err);
                                        }
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 text-xs text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg w-full text-left transition-colors"
                                >
                                    <RotateCcw size={13} />
                                    Restart setup guide
                                </button>
                            </div>
                        )}
                        </>) /* end of non-locked nav */}
                    </nav>
                </div>

                {/* --- MAIN CONTENT AREA --- */}
                <div className={`w-full min-w-0 lg:flex-1 ${fullWidthTab ? '' : 'max-w-4xl'}`}>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 min-h-[500px]">
                        {renderTabContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;